import moment from 'moment';
import { buildWidgetConfig } from '../services/meterApi';
import { findKpiForYear } from './meterFilters';

/** Days of history a card's sparkline covers. */
export const TREND_DAYS = 14;

/**
 * Builds one widget request covering every meter's daily consumption.
 *
 * Grouping by meter_id returns all series in a single call, so a grid of twenty
 * cards costs one request rather than twenty.
 *
 * @param {Array<object>} meters - Meters currently loaded.
 * @param {string} financialYear - Financial year the KPI mapping is read from.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {object|null} Widget config, or null when there is nothing to ask for.
 */
export const buildTrendConfig = (meters = [], financialYear, now = () => moment()) => {
  const modules = Array.from(
    new Set(
      meters
        .map((meter) => findKpiForYear(meter, financialYear)?.module)
        .filter(Boolean)
    )
  );

  if (!modules.length) return null;

  return buildWidgetConfig({
    module: modules[0],
    kpiType: 'EXTERNAL',
    measurementType: 'CUMULATIVE',
    financialYear,
    startDateTime: now().clone().subtract(TREND_DAYS, 'days').startOf('day').toISOString(),
    endDateTime: now().toISOString(),
    aggregationPeriodValue: 1,
    aggregationPeriodUnit: 'days',
    locationId: '',
    xAxisField: 'displayPeriods',
    stackByField: 'meter_id',
    title: 'Meter trends',
  });
};

/**
 * Turns the flat widget response into one ordered series per meter.
 *
 * The API aliases `meter_name AS meter_id` when grouping, so rows are keyed by
 * the meter's NAME — matching on meterId would find nothing.
 *
 * @param {Array<object>} rows - Rows returned by the widget endpoint.
 * @returns {Object<string, Array<number>>} Meter name to its ordered daily values.
 */
export const groupTrendsByMeter = (rows = []) => {
  const byMeter = new Map();

  rows.forEach((row) => {
    const name = row.meter_id;
    if (!name) return;

    if (!byMeter.has(name)) byMeter.set(name, []);
    byMeter.get(name).push({
      at: row.period_ts ? new Date(row.period_ts).getTime() : 0,
      value: Number(row.sum_value ?? row.value ?? 0),
    });
  });

  const series = {};
  byMeter.forEach((points, name) => {
    series[name] = points
      .sort((a, b) => a.at - b.at)
      .map((point) => (Number.isFinite(point.value) ? point.value : 0));
  });

  return series;
};

/**
 * Reports whether a series is worth drawing.
 * A single point cannot form a line, and an all-zero series is drawn flat
 * rather than discarded — a meter recording nothing is a finding, not a gap.
 * @param {Array<number>} values - Ordered daily values.
 * @returns {boolean} True when the sparkline should render.
 */
export const isDrawableTrend = (values) =>
  Array.isArray(values) && values.length >= 2;
